import { useState, useEffect } from "react";
import { ChevronLeft } from "lucide-react";
import DropdownBox from "../layout/DropdownBox";
import { RadixIconsCopy } from "../../assets/icons/icons";
import ConfirmationDialogBox from "../layout/ConfirmationDialogBox";
import { toast } from "react-toastify";
import {
  editPromoCode,
  validatePromoCode,
} from "../../actions/PromoCodeAction";
import isEqual from "lodash.isequal";

const EditPromoCode = ({ handleBackToTable, retailer }) => {
  const [formData, setFormData] = useState({
    referralId: retailer._id,
    retailerId: retailer.retailerId || "",
    retailerName: retailer.retailerName || "",
    retailerEmail: retailer.retailerEmail || "",
    referralCode: retailer.code || "",
    status: retailer.status || "",
  });
  const [errors, setErrors] = useState({});
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Track form changes
  useEffect(() => {
    const { retailerId, retailerName, retailerEmail } = formData;

    const hasChanges =
      retailerId.trim() !== retailer.retailerId.trim() ||
      retailerName.trim() !== retailer.retailerName.trim() ||
      retailerEmail.trim() !== retailer.retailerEmail.trim() ||
      formData.referralCode !== retailer.code ||
      formData.status !== retailer.status;

    setHasUnsavedChanges(hasChanges);
  }, [formData]);

  // Handle input changes
  const handleInputChange = async (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }

    // Validate referral code when it reaches 4 digits
    if (field === "referralCode" && value.length === 4) {
      try {
        const response = await validatePromoCode(value);
        if (response?.success) {
          toast.success("Referral code is valid");
        } else {
          setErrors((prev) => ({
            ...prev,
            referralCode: response?.message || "Invalid referral code",
          }));
          toast.error(response?.message || "Invalid referral code");
        }
      } catch (error) {
        console.error("Error validating referral code:", error);
        setErrors((prev) => ({
          ...prev,
          referralCode: "Error validating referral code",
        }));
        toast.error("Error validating referral code");
      }
    }
  };

  // Copy referral code to clipboard
  const copyToClipboard = async () => {
    if (formData.referralCode) {
      try {
        await navigator.clipboard.writeText(formData.referralCode);
        toast.success("Code copied to clipboard");
      } catch (error) {
        toast.error("Failed to copy:", error);
      }
    }
  };

  // Validate form
  const validateForm = () => {
    if (!/\S+@\S+\.\S+/.test(formData.retailerEmail)) {
      toast.error("Invalid email format");
      return;
    }
    const newErrors = {};

    if (!formData.retailerId.trim()) {
      newErrors.retailerId = "Retailer ID ";
    }

    if (!formData.retailerName.trim()) {
      newErrors.retailerName = "Retailer name";
    }

    if (!formData.retailerEmail.trim()) {
      newErrors.retailerEmail = "Email address";
    }

    if (!formData.referralCode) {
      newErrors.referralCode = "Referral code ";
    }

    if (!formData.status) {
      newErrors.status = "Status";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const combinedMessage = Object.values(newErrors).join(" , ");
      toast.error(`Required field : ${combinedMessage} `);
      return false;
    }

    return true;
  };

  // Check if field has error
  const isFieldMissing = (fieldName) => {
    return errors[fieldName] ? true : false;
  };

  // Get input border class based on validation
  const getInputBorderClass = (fieldName) => {
    return isFieldMissing(fieldName) ? "border-red-500" : "border-gray-300";
  };

  // Handle back button click
  const handleBack = () => {
    if (hasUnsavedChanges) {
      setConfirmDialog(true);
      return;
    }
    handleBackToTable();
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    // Construct payload with only changed fields
    let hasChanges = false;
    const payload = { referralId: formData.referralId };

    Object.keys(formData).forEach((key) => {
      if (key === "referralId") return; // Skip referralId as it's already included

      const originalValue =
        retailer[key] || retailer[key === "referralCode" ? "code" : key] || "";
      const newValue = formData[key];

      if (!isEqual(newValue, originalValue)) {
        payload[key] = newValue;
        hasChanges = true;
      }
    });

    if (!hasChanges) {
      toast.info("No changes detected");
      return;
    }

    setIsLoading(true);
    try {
      // console.log("Submitting changed fields:", payload);
      const response = await editPromoCode(payload);
      if (response.success) {
        toast.success("Referral code updated successfully");
        setHasUnsavedChanges(false);
        handleBackToTable();
      } else {
        toast.error(response.message || "Failed to update referral code");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong while updating the referral code");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle confirmation dialog
  const handleCancel = () => {
    setConfirmDialog(false);
  };

  const handleConfirm = () => {
    setConfirmDialog(false);
    setHasUnsavedChanges(false);
    handleBackToTable();
  };

  return (
    <>
      <div>
        {/* Header with mobile-only back button and responsive styles */}
        <div className="flex justify-between items-center gap-3 mb-4">
          <div className="flex items-center gap-2">
            <span className="sm:hidden">
              <button onClick={handleBack} className="p-1 rounded hover:bg-gray-100 focus:outline-none">
                <ChevronLeft className="h-6 w-6 text-text1" strokeWidth={3} />
              </button>
            </span>
            <span className="font-bold text-text1 text-lg sm:text-[30px]">Edit referral code</span>
          </div>
          <div className="flex gap-2">
            <button className="secondary_button inactive px-3 py-1 text-xs sm:px-6 sm:py-2 sm:text-base" onClick={handleBack} disabled={isLoading}>Cancel</button>
            <button className="secondary_button active px-3 py-1 text-xs sm:px-6 sm:py-2 sm:text-base" onClick={handleSubmit} disabled={isLoading}>{isLoading ? "Updating..." : "Update"}</button>
          </div>
        </div>
        <hr className="border-gray-300 mt-4" />

        {/* Form Section */}
        <div className="py-2">
          <div className="flex flex-col md:flex-row w-full gap-4">
            <div className="flex-1 mb-2">
              <label className="md:font-medium font-normal text-sm leading-5 text-[#374151]">Retailer ID<span className="text-required">*</span></label>
              <input type="text" placeholder="Enter retailer ID" value={formData.retailerId} onChange={(e) => handleInputChange("retailerId", e.target.value)} className={`required_input text-text1 w-full p-2 border ${getInputBorderClass("retailerId")} rounded mt-1 focus:outline-none`} />
            </div>
            <div className="flex-1 mb-2">
              <label className="md:font-medium font-normal text-sm leading-5 text-[#374151]">Retailer name<span className="text-required">*</span></label>
              <input type="text" placeholder="Enter retailer name" value={formData.retailerName} onChange={(e) => handleInputChange("retailerName", e.target.value)} className={`required_input text-text1 w-full p-2 border ${getInputBorderClass("retailerName")} rounded mt-1 focus:outline-none`} />
            </div>
            <div className="flex-1 mb-2">
              <label className="md:font-medium font-normal text-sm leading-5 text-[#374151]">Email address<span className="text-required">*</span></label>
              <input type="email" placeholder="Enter email address" value={formData.retailerEmail} onChange={(e) => handleInputChange("retailerEmail", e.target.value)} className={`required_input text-text1 w-full p-2 border ${getInputBorderClass("retailerEmail")} rounded mt-1 focus:outline-none`} />
            </div>
          </div>
          <div className="flex flex-col md:flex-row w-full gap-4">
            <div className="flex-1 mb-2">
              <label className="md:font-medium font-normal text-sm leading-5 text-[#374151]">Referral code<span className="text-required">*</span></label>
              <div className={`flex items-center mt-1 border ${getInputBorderClass("referralCode")} rounded-md`}>
                <input type="text" placeholder="Enter or generate referral code" value={formData.referralCode} onChange={(e) => handleInputChange("referralCode", e.target.value.replace(/[^0-9]/g, "").slice(0, 4))} maxLength={4} className="text-text1 w-full border-r border-gray-300 rounded-l-md py-[10px] px-[14px] focus:outline-none" />
                <div className="flex items-center px-2 py-2 cursor-pointer hover:bg-gray-50" onClick={copyToClipboard} title="Copy to clipboard">
                  <RadixIconsCopy className="text-gray-600 h-6 w-6" />
                </div>
              </div>
            </div>
            <div className="flex-1 mb-2">
              <label className="md:font-medium font-normal text-sm leading-5 text-[#374151]">Status<span className="text-required">*</span></label>
              <DropdownBox optionList={["ACTIVE", "INACTIVE"]} value={formData.status} handleChange={(value) => handleInputChange("status", value)} styleName={`rounded px-1 text-sm font-normal text-text1 focus:outline-none focus:ring-0 w-full mt-1 border ${getInputBorderClass("status")}`} placeholder="Select status" />
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <ConfirmationDialogBox
          handleCancel={handleCancel}
          handleConfirm={handleConfirm}
          message="You have unsaved changes. <strong>If you leave or refresh this page, any unsaved data will be lost.</strong> Would you like to save your changes before proceeding?"
          type="warning"
          disabled={isLoading}
        />
      )}
    </>
  );
};

export default EditPromoCode;
